import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { AddressInput } from '@/components/ui/AddressInput'
import { useYandexMaps } from '@/hooks/useYandexMaps'
import { updateInspectorPosition } from '@/api/inspectors_positions'

interface Props {
  inspectorId: string
  inspectorName: string
  onClose: () => void
}

interface YmapsLike {
  geocode: (
    request: string,
    opts?: { results?: number; boundedBy?: number[][] },
  ) => Promise<{
    geoObjects: {
      get: (i: number) => {
        geometry: { getCoordinates: () => [number, number] }
      } | undefined
    }
  }>
}

/**
 * Модалка «Изменить позицию инспектора {ФИО}».
 * Видна только админу (вызывается со страницы под if (isAdmin)).
 *
 * Поведение:
 *  1. AddressInput даёт Geosuggest-подсказки (тот же сервис, что для адреса
 *     заказа). У подсказок нет координат — после выбора делаем `ymaps.geocode`
 *     по выбранному value, чтобы получить (lat, lon).
 *  2. На «Сохранить» — `updateInspectorPosition` (RLS: admin only).
 *     Realtime-канал на странице сам подхватит UPDATE и обновит строку.
 */
export function EditInspectorPositionModal({
  inspectorId,
  inspectorName,
  onClose,
}: Props) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { ready: ymapsReady, isConfigured } = useYandexMaps()

  const [address, setAddress] = useState('')
  const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [resolving, setResolving] = useState(false)

  async function geocodeAddress(query: string): Promise<{ lat: number; lon: number } | null> {
    if (!ymapsReady) return null
    const ymaps = (window as unknown as { ymaps?: YmapsLike }).ymaps
    if (!ymaps?.geocode) return null
    try {
      const res = await ymaps.geocode(query, { results: 1 })
      const first = res.geoObjects.get(0)
      if (!first) return null
      // Yandex Maps JS API в WGS84-режиме (по умолчанию) возвращает [lat, lon],
      // НЕ [lon, lat]. Это известная инверсия Яндекса относительно Google/MapBox.
      // Если перепутать — точки из Москвы улетают в Иран (37°N 55°E вместо 55°N 37°E).
      const [lat, lon] = first.geometry.getCoordinates()
      return { lat, lon }
    } catch (e) {
      console.error('[EditInspectorPositionModal] geocode failed', e)
      return null
    }
  }

  const mut = useMutation({
    mutationFn: async () => {
      let resolved = coords
      if (!resolved && address.trim()) {
        // Если выбрали подсказку давно, а потом дописали что-то руками —
        // всё равно попробуем геокодировать строку как есть.
        setResolving(true)
        try {
          resolved = await geocodeAddress(address.trim())
        } finally {
          setResolving(false)
        }
      }
      if (!resolved) {
        throw new Error(t('geo.editPos.geocodeFailed'))
      }
      await updateInspectorPosition(
        inspectorId,
        resolved.lat,
        resolved.lon,
        address.trim() || null,
      )
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inspectors-positions'] })
      onClose()
    },
    onError: (e: Error) => setError(e.message),
  })

  return (
    <Modal
      title={t('geo.editPos.title', { name: inspectorName })}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mut.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => mut.mutate()}
            disabled={mut.isPending || resolving || !address.trim() || !ymapsReady}
          >
            {mut.isPending || resolving ? t('geo.editPos.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {!isConfigured ? (
          <div className="rounded border border-yellow-500/40 bg-yellow-500/10 p-3 text-sm text-yellow-200">
            {t('geo.editPos.noApiKey')}
          </div>
        ) : null}
        <AddressInput
          label={t('geo.editPos.addressLabel')}
          value={address}
          onChange={(v) => {
            setAddress(v)
            setCoords(null)
            setError(null)
          }}
          placeholder={t('geo.editPos.addressPlaceholder')}
          onSuggestionPicked={async (item) => {
            setAddress(item.line1)
            // Geosuggest-карточки координат не возвращают — догеокодим.
            setResolving(true)
            try {
              const got = await geocodeAddress(item.value)
              setCoords(got)
              if (!got) setError(t('geo.editPos.pickedGeocodeFailed'))
            } finally {
              setResolving(false)
            }
          }}
        />

        {coords ? (
          <div className="text-sm text-[color:var(--stryzh-color-text-secondary)]">
            {t('geo.editPos.coords')}{' '}
            <code className="font-mono">
              {coords.lat.toFixed(6)}, {coords.lon.toFixed(6)}
            </code>
          </div>
        ) : address.trim() && !resolving ? (
          <div className="text-xs text-[color:var(--stryzh-color-text-secondary)]">
            {t('geo.editPos.coordsOnSave')}
          </div>
        ) : null}

        {error ? (
          <div className="rounded border border-red-500/40 bg-red-500/10 p-2 text-sm text-red-200">
            {error}
          </div>
        ) : null}
      </div>
    </Modal>
  )
}
